'use client';

import type { LottieRefCurrentProps } from 'lottie-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LazyLottie as Lottie } from '@/lib/ui-kit/lazy-lottie';
import { cn } from '@/lib/ui-kit/utils';

interface LottieIllustrationProps {
  /** file name under /public/animations, without the .json extension */
  name: string;
  /** square size in px (reserved while loading to avoid layout shift) */
  size?: number;
  /**
   * visible height in px — crops the composition's empty padding (the lottie
   * canvas stays `size` tall and is center-cropped unless offsetY is given)
   */
  height?: number;
  /** vertical shift of the composition inside the crop, in px (negative = up) */
  offsetY?: number;
  className?: string;
  /** play in a loop; by default the illustration plays once */
  loop?: boolean;
  /**
   * play only [from, to] frames and freeze at `to` — use when a composition's
   * tail animates the artwork out (freezing on the last frame would be empty)
   */
  segment?: [number, number];
  /**
   * After the first full play, loop just these frames — an entrance that
   * should happen once, then an idle glint that repeats.
   */
  loopSegment?: [number, number];
  /**
   * Fill the parent instead of a fixed `size` — for an overlay that has to
   * track a box sized in CSS (an avatar resized per breakpoint, say).
   */
  fill?: boolean;
}

// Module-level cache: reopening a dialog replays the animation without
// refetching the JSON (some files exceed 1 MB). Caching the promise lets
// concurrent mounts share a single request; failures are evicted so the
// next mount can retry.
const animationCache = new Map<string, Promise<object>>();

function loadAnimation(name: string): Promise<object> {
  let cached = animationCache.get(name);
  if (!cached) {
    cached = fetch(`/animations/${name}.json`).then((res) => res.json());
    cached.catch(() => animationCache.delete(name));
    animationCache.set(name, cached);
  }
  return cached;
}

export function preloadAnimation(name: string) {
  void loadAnimation(name);
}

/** A /public/animations file's JSON, `null` until it has loaded — for a player driven by hand. */
export function useAnimationData(name: string): object | null {
  const [data, setData] = useState<object | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadAnimation(name)
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [name]);

  return data;
}

/**
 * Dialog/page illustration driven by a Lottie file from /public/animations.
 * Animations are fetched at runtime (they are too heavy to bundle) and play
 * once per mount — reopening a dialog remounts and replays it, but the JSON
 * comes from the module cache after the first load.
 */
export function LottieIllustration({
  name,
  size = 120,
  height,
  offsetY,
  className,
  loop = false,
  segment,
  loopSegment,
  fill = false,
}: LottieIllustrationProps) {
  const data = useAnimationData(name);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const handleComplete = useCallback(() => {
    if (loopSegment) lottieRef.current?.playSegments(loopSegment, true);
  }, [loopSegment]);

  const visibleHeight = height ?? size;
  const shift = offsetY ?? (height ? (height - size) / 2 : 0);

  return (
    <div
      className={cn('overflow-hidden pointer-events-none select-none', className)}
      style={fill ? undefined : { width: size, height: visibleHeight }}
      aria-hidden
    >
      {data && (
        <div
          style={
            fill
              ? { width: '100%', height: '100%' }
              : { width: size, height: size, marginTop: shift }
          }
        >
          <Lottie
            lottieRef={lottieRef}
            animationData={data}
            loop={loopSegment ? false : loop}
            autoplay
            initialSegment={segment}
            onComplete={loopSegment ? handleComplete : undefined}
          />
        </div>
      )}
    </div>
  );
}
